import { useLayoutEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { returnUser } from '@util/jwt/token';

import { toast } from 'react-toastify';
import useUserStore from 'store/user.store';
import _ from 'lodash';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { InferType } from 'yup';
import { useRegisterPasswordMutation } from '@common/cache';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { registerSchema } from '../schemas';

const initialValues: IFormShape = { email: '', password: '', confirm: '' };

type IFormShape = InferType<typeof registerSchema>;

export const useRegister = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string | null>(null);

  const { setRegisterToken } = useUserStore();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const navigate = useNavigate();

  const mutation = useRegisterPasswordMutation();

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(registerSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = formMethods;

  const submitHandler = async (values: IFormShape) => {
    try {
      await mutation.mutateAsync({ token: token!, password: values.password });
      setRegisterToken(token!);
      navigate('/auth/mfa', { replace: true });
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  useLayoutEffect(() => {
    try {
      const token = searchParams.get('token');
      if (!token) throw Error('No register token');

      const { email } = returnUser(token!);

      if (!email) throw Error('No email in token');

      setToken(token);
      setValue('email', email);
    } catch (error) {
      toast.error(formatApiError(error));
      navigate('/auth/login');
    }
  }, [searchParams, navigate]);

  return {
    toggleShowPassword,
    onSubmit,
    formMethods,
    loading: isSubmitting || mutation.isPending,
    showPassword,
  };
};
